import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { ExpenseCategoryDTO } from "../api/client";
import {
  useCreateExpenseCategoryMutation,
  useExpenseCategoriesQuery,
  useUpdateExpenseCategoryMutation,
} from "../api/queries";
import {
  ExpenseCategoriesFilter,
  type ExpenseCategoriesFilterState,
} from "../ui/ExpenseCategoriesFilter";
import { ExpenseCategoriesTable } from "../ui/ExpenseCategoriesTable";
import { ExpenseCategoryFormDialog } from "../ui/ExpenseCategoryFormDialog";
import { ExpenseCategorySummaryCards } from "../ui/ExpenseCategorySummaryCards";

function filterCategories(
  categories: ExpenseCategoryDTO[],
  filters: ExpenseCategoriesFilterState,
) {
  const query = filters.search.trim().toLowerCase();

  return categories.filter((category) => {
    if (filters.status === "active" && !category.isActive) return false;
    if (filters.status === "inactive" && category.isActive) return false;
    if (!query) return true;

    return (
      category.code.toLowerCase().includes(query) ||
      category.name.toLowerCase().includes(query)
    );
  });
}

export function ExpenseCategoriesPage() {
  const nav = useNavigate();
  const canManage = useActionPermission(["expenses:manage-categories"]);
  const { data: categories = [], isLoading } = useExpenseCategoriesQuery();
  const createMutation = useCreateExpenseCategoryMutation();
  const updateMutation = useUpdateExpenseCategoryMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategoryDTO | null>(null);
  const [filters, setFilters] = useState<ExpenseCategoriesFilterState>({
    search: "",
    status: "all",
  });

  const filteredCategories = useMemo(
    () => filterCategories(categories, filters),
    [categories, filters],
  );

  const emptyMessage = categories.length === 0 ? "ຍັງບໍ່ມີຫມວດ" : "ບໍ່ພົບຫມວດຕາມຕົວກອງ";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/expenses/overview" },
            { label: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ", to: "/app/expenses" },
            { label: "ຫມວດຄ່າໃຊ້ຈ່າຍ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">
                ຫມວດຄ່າໃຊ້ຈ່າຍ
              </h1>
              <p className="mt-1 text-muted-foreground text-sm">
                ຈັດການຫມວດສຳລັບຈັດກຸ່ມ ແລະ ເລືອກໃນຟອມບັນທຶກຄ່າໃຊ້ຈ່າຍ
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/expenses" })}
              >
                <ArrowLeftIcon className="mr-2 size-4" />
                ກັບລາຍການຄ່າໃຊ້ຈ່າຍ
              </Button>
              {canManage ? (
                <Button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  <PlusIcon className="mr-2 size-4" />
                  ເພີ່ມຫມວດ
                </Button>
              ) : null}
            </div>
          </div>

          <ExpenseCategorySummaryCards
            categories={categories}
            isLoading={isLoading}
            statusFilter={filters.status}
            onStatusFilterChange={(status) =>
              setFilters((current) => ({ ...current, status }))
            }
          />

          <Card className="gap-0 py-0">
            <CardHeader className="space-y-1 border-b px-6 py-4">
              <CardTitle className="text-base">ລາຍການຫມວດ</CardTitle>
              <CardDescription>
                ລະຫັດໃຊ້ອ້າງອີງໃນລະບົບ — ຊື່ແມ່ນສິ່ງທີ່ຜູ້ໃຊ້ເຫັນໃນຟອມ
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0">
              <ExpenseCategoriesFilter
                filters={filters}
                onFiltersChange={setFilters}
                filteredCount={filteredCategories.length}
                totalCount={categories.length}
              />

              <ExpenseCategoriesTable
                data={filteredCategories}
                isLoading={isLoading}
                canManage={!!canManage}
                emptyMessage={emptyMessage}
                onEdit={(row) => {
                  setEditing(row);
                  setDialogOpen(true);
                }}
              />
            </CardContent>
          </Card>
        </div>

        <ExpenseCategoryFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initial={editing}
          isPending={createMutation.isPending || updateMutation.isPending}
          onSubmit={async (values) => {
            const payload = {
              code: values.code.trim().toUpperCase(),
              name: values.name.trim(),
              sortOrder: values.sortOrder ?? 0,
              isActive: values.isActive,
            };

            if (editing) {
              await updateMutation.mutateAsync({
                id: editing.id,
                data: {
                  name: payload.name,
                  sortOrder: payload.sortOrder,
                  isActive: payload.isActive,
                },
              });
            } else {
              await createMutation.mutateAsync(payload);
            }
          }}
        />
      </Main>
    </>
  );
}
